/**
 * /apps/:appId/setup/roles
 *
 * View-first list of roles. Click anywhere on a row to open the role
 * detail page (where ALL editing — areas, skills, education, experience,
 * best performance — happens). The "+ Add Role" button creates a new role
 * with just basic identity fields; everything else is filled in on the
 * detail page.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import EntityFormModal from '../../components/workspace/EntityFormModal';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { formatRupeesShort, formatRupeesFull } from '../../lib/format';

const EMPLOYMENT_OPTIONS = [
  { value: 'full_time',  label: 'Full-time' },
  { value: 'part_time',  label: 'Part-time' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'intern',     label: 'Intern' },
  { value: 'consultant', label: 'Consultant' },
];

const EMPLOYMENT_LABEL = Object.fromEntries(
  EMPLOYMENT_OPTIONS.map((o) => [o.value, o.label])
);

const rolesCache = new Map(); // appId → { roles, departments }

export default function Roles() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const { canWrite } = useWorkspace();

  // api reference changes when Clerk refreshes its token — use a ref so
  // load() stays stable and doesn't retrigger the useEffect on every render.
  const apiRef = useRef(api);
  apiRef.current = api;

  const cached = rolesCache.get(appId);
  const [roles, setRoles]                       = useState(cached?.roles       || []);
  const [deactivatedRoles, setDeactivatedRoles]  = useState(cached?.deactivatedRoles || []);
  const [departments, setDepartments]            = useState(cached?.departments || []);
  const [loading, setLoading]                    = useState(!cached);
  const [error, setError]                        = useState(null);
  const [createOpen, setCreateOpen]              = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [rs, ds] = await Promise.all([
        apiRef.current.roles.list(appId, { includeInactive: 1 }),
        apiRef.current.departments.list(appId).catch(() => ({ items: [] })),
      ]);
      const allRoles = rs.items || [];
      const roleList = allRoles.filter((r) => !r.deleted_at);
      const deactivatedList = allRoles.filter((r) => !!r.deleted_at);
      const deptList = ds.items || [];
      setRoles(roleList);
      setDeactivatedRoles(deactivatedList);
      setDepartments(deptList);
      rolesCache.set(appId, { roles: roleList, deactivatedRoles: deactivatedList, departments: deptList });
    } catch (err) {
      if (!silent) setError(err.response?.data?.error || err.message || 'Failed to load roles');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [appId]);

  const handleReactivate = async (roleId) => {
    setError(null);
    try {
      await api.reactivateRole(appId, roleId);
      await load(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to reactivate role');
    }
  };

  useEffect(() => {
    load(!!cached);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const deptLookup = departments.map((d) => ({ value: String(d.id), label: d.name }));
  const roleLookup = roles.map((r)        => ({ value: String(r.id), label: r.title }));

  // Only basic identity on create — the rest lives on the detail page.
  const reportsToRequired = roles.length > 0;
  const CREATE_FIELDS = [
    { name: 'title', label: 'Title', required: true, maxLen: 120, alphanumeric: true },
    { name: 'code',  label: 'Code',  maxLen: 50, alphanumeric: true },
    { name: 'department_id',      label: 'Department',     type: 'lookup', lookupOptions: deptLookup },
    { name: 'level',              label: 'Level',          type: 'number', min: 0, max: 20, help: 'Seniority band 0..20' },
    { name: 'reports_to_role_id', label: 'Reports to',     type: 'lookup', lookupOptions: roleLookup, required: reportsToRequired },
    { name: 'employment_type',    label: 'Employment',     type: 'select', options: EMPLOYMENT_OPTIONS, required: true },
    { name: 'max_yearly_ctc',     label: 'Max yearly CTC', type: 'number', min: 0, max: 100000000, help: 'Max annual cost-to-company (up to 10 Cr)' },
  ];

  const handleCreate = async (body) => {
    const created = await api.roles.create(appId, body);
    // Take the user straight to the new role's detail page so they can
    // start adding areas, skills, etc.
    navigate(`/apps/${appId}/setup/roles/${created.id}`);
  };

  if (loading) return <p className="muted" style={{ padding: 24 }}>Loading roles…</p>;

  return (
    <>

      <section className="entity-section">
        <div className="entity-head">
          <h1 className="page-title">Roles</h1>
          {canWrite && (
            <button className="btn-primary" onClick={() => setCreateOpen(true)}>+ Add Role</button>
          )}
        </div>

        <p className="muted" style={{ marginBottom: 14 }}>
          Click a role to open its full definition — major areas, required
          skills, education, experience, and best-performance benchmark.
        </p>

        {error && <div className="banner banner-error">{error}</div>}

        {roles.length === 0 ? (
          <div className="empty-state-small">
            No roles yet. Add the first role — this is the keystone for the rest
            of the workspace.
          </div>
        ) : (
          <div className="entity-table-wrap">
            <table className="entity-table roles-list">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Sl No</th>
                  <th style={{ width: 180 }}>Title</th>
                  <th style={{ width: 90 }}>Code</th>
                  <th style={{ width: 160 }}>Department</th>
                  <th style={{ width: 70 }}>Level</th>
                  <th style={{ width: 160 }}>Reports to</th>
                  <th style={{ width: 120 }}>Employment</th>
                  <th style={{ width: 130 }}>Max CTC / yr</th>
                  <th style={{ width: 260 }}>Profile Progress</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((r, idx) => {
                  // Weighted completion score (must sum to 100)
                  const checks = [
                    { weight: 10, done: !!(r.purpose_md?.trim() || r.description?.trim()) },
                    { weight: 20, done: (r.areas_count      || 0) > 0 },
                    { weight: 20, done: (r.skills_count     || 0) > 0 },
                    { weight: 15, done: (r.routines_count   || 0) > 0 },
                    { weight: 10, done: (r.education_count  || 0) > 0 },
                    { weight: 10, done: (r.experience_count || 0) > 0 },
                    { weight: 15, done: !!(r.best_performance_md?.trim()) },
                  ];
                  const pct = checks.reduce((s, c) => s + (c.done ? c.weight : 0), 0);
                  const barColor = pct >= 80 ? '#4ad295' : pct >= 50 ? '#f0a93b' : '#ff6b6b';
                  return (
                    <tr
                      key={r.id}
                      className="row-clickable"
                      onClick={() => navigate(`/apps/${appId}/setup/roles/${r.id}`)}
                    >
                      <td className="serial-cell">{idx + 1}</td>
                      <td title={r.title}><strong>{r.title}</strong></td>
                      <td title={r.code}>{r.code || <span className="muted">—</span>}</td>
                      <td title={departments.find((d) => d.id === r.department_id)?.name}>{departments.find((d) => d.id === r.department_id)?.name || <span className="muted">—</span>}</td>
                      <td>{r.level ?? 0}</td>
                      <td title={roles.find((x) => x.id === r.reports_to_role_id)?.title}>{roles.find((x) => x.id === r.reports_to_role_id)?.title || <span className="muted">—</span>}</td>
                      <td title={EMPLOYMENT_LABEL[r.employment_type] || r.employment_type}>{EMPLOYMENT_LABEL[r.employment_type] || r.employment_type}</td>
                      <td title={formatRupeesFull(r.max_yearly_ctc)}>{formatRupeesShort(r.max_yearly_ctc) || <span className="muted">—</span>}</td>
                      <td onClick={e => e.stopPropagation()} style={{ cursor: 'default', overflow: 'visible' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 6, borderRadius: 99, background: 'var(--bg-muted, #e5e7eb)', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 99, transition: 'width 0.3s' }} />
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 700, color: barColor, minWidth: 32, textAlign: 'right' }}>{pct}%</span>
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>
                          {checks.map((c, i) => {
                            const labels = ['Purpose','Areas','Skills','Routines','Education','Experience','Best Performance'];
                            return !c.done ? (
                              <span key={i} style={{ marginRight: 4, opacity: 0.7 }}>· {labels[i]}</span>
                            ) : null;
                          })}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <EntityFormModal
          open={createOpen}
          title="Add Role"
          fields={CREATE_FIELDS}
          initial={null}
          onSubmit={handleCreate}
          onClose={() => setCreateOpen(false)}
          submitLabel="Create Role"
        />
      </section>

      {deactivatedRoles.length > 0 && (
        <section className="entity-section" style={{ marginTop: 24 }}>
          <div className="entity-head">
            <h2 className="page-title-sm" style={{ color: '#888' }}>Deactivated Roles</h2>
          </div>
          <div className="entity-table-wrap">
            <table className="entity-table roles-list">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Sl No</th>
                  <th>Title</th>
                  <th style={{ width: 90 }}>Code</th>
                  <th style={{ width: 160 }}>Department</th>
                  {canWrite && <th style={{ width: 1 }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {deactivatedRoles.map((r, idx) => (
                  <tr key={r.id} style={{ opacity: 0.65 }}>
                    <td className="serial-cell">{idx + 1}</td>
                    <td title={r.title}><strong>{r.title}</strong></td>
                    <td title={r.code}>{r.code || <span className="muted">—</span>}</td>
                    <td>{departments.find((d) => d.id === r.department_id)?.name || <span className="muted">—</span>}</td>
                    {canWrite && (
                      <td className="row-actions">
                        <button className="btn-link-sm activate" onClick={() => handleReactivate(r.id)}>
                          Activate
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
